/**
 * Drop-in replacement for React.lazy that recovers from stale-chunk failures.
 *
 * After a deploy, a long-lived tab still runs the old index.html, which
 * references asset hashes that no longer exist on S3. The next route-level
 * dynamic import then 404s with a ChunkLoadError. This wrapper:
 *   1. retries the import once (covers a transient network blip), then
 *   2. reloads the page once to pull a fresh index.html with current hashes.
 *
 * A sessionStorage flag guards against an infinite reload loop when the chunk
 * is genuinely missing (e.g. a real build error); the flag is cleared on any
 * successful import so a later legitimate stale-chunk reload can still fire.
 *
 * This is the web counterpart to the cache-control invariant in
 * docs/infrastructure/ci-pipeline-and-hooks.md: old hashed chunks linger on S3
 * (never `--delete`d) as a grace buffer, and the reload covers the window after
 * that buffer or when index.html itself was already refreshed.
 */
import { lazy, type ComponentType, type LazyExoticComponent } from 'react';

const RELOAD_FLAG = 'panluma:lazy-retry-reloaded';

// Mirror React.lazy's own signature exactly. `ComponentType<unknown>` would
// reject components with required props (props are contravariant), so — like
// React's built-in typings — we use `any` for the props parameter here.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
): LazyExoticComponent<T> {
  return lazy(async () => {
    try {
      const module = await factory();
      sessionStorage.removeItem(RELOAD_FLAG);
      return module;
    } catch {
      // One retry for a transient network blip before assuming a stale chunk.
      try {
        const module = await factory();
        sessionStorage.removeItem(RELOAD_FLAG);
        return module;
      } catch (retryError) {
        // Likely a stale chunk after a deploy. Reload once to fetch a fresh
        // index.html with current asset hashes — but only once, to avoid an
        // infinite reload loop if the chunk is genuinely gone.
        if (!sessionStorage.getItem(RELOAD_FLAG)) {
          sessionStorage.setItem(RELOAD_FLAG, '1');
          window.location.reload();
          // The reload replaces the page; never resolve so React doesn't flash
          // an error boundary before the navigation completes.
          return new Promise<{ default: T }>(() => {});
        }
        throw retryError;
      }
    }
  });
}
